import {
  useIsRpaAgentEnabled,
  useIsWorkInSessionEnabled,
} from '@/app-shell/flags/agents-flags';
import { isEnglishMarket } from '@/app-shell/flags/market';
import { posthog, useFeatureFlagEnabled } from '@/app-shell/flags/posthog-compat';
import { IS_TRANSCRIPT_CHIPS_ENABLED } from '@/app-shell/flags/session-flags';
import { sessionlessEvidenceAwarenessDevVariantOverrideAtom } from '@/atoms/sessionlessEvidenceAwarenessDevAtoms';
import { useManualExposure } from '@heidi/analytics/events';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { FEATURE_FLAG_DOMAIN } from '@heidi/runtime/feature-flags';
import { Region, SignupSource } from '@heidi/schema/ml-scribe';
import { OpenFeature, useBooleanFlagValue } from '@openfeature/react-sdk';
import { getDefaultStore } from 'jotai';
import { z } from 'zod';

/**
 * GRO-2846: Displays the Evidence search bar in a central location
 * within the empty note area during transcription.
 *
 * Non-hook variant for usage outside React components/hooks.
 */
export const IS_EVIDENCE_CENTRAL_SEARCH_BAR_ENABLED = () => {
  return posthog.isFeatureEnabled('experiment-evidence-central-search-bar') ?? false;
};

/**
 * PostHog multivariate experiment: in-session document drafting from clinical tasks.
 * - control: off
 * - banner: carousel banner above input
 * - chips: document suggestions merged into the suggestions peek sheet as chips
 */
export type EvidenceDocumentSuggestionExperimentVariant = 'control' | 'banner' | 'chips';

export const GET_EVIDENCE_DOCUMENT_SUGGESTION_EXPERIMENT_VARIANT =
  (): EvidenceDocumentSuggestionExperimentVariant => {
    const schema = z.enum(['control', 'banner', 'chips']).catch('control');
    return schema.parse(posthog.getFeatureFlag('exp-evidence-document-suggestion'));
  };

/** PostHog: gates doc export suggestions in Evidence using attached evidence. */
export const IS_EVIDENCE_DOC_EXPORT_SUGGESTIONS_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

export const IS_PROTOCOLS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-protocols');
};

export const IS_CREATE_TEMPLATE_MODAL_VIA_CHAT_ENABLED = () => {
  return posthog.isFeatureEnabled('release-create-template-modal-via-chat');
};

export const IS_ASK_HEIDI_V2_CHAT_COMPLETIONS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-ask-heidi-v2-chat-completions') ?? false;
};

export const IS_SESSIONLESS_EVIDENCE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-sessionless-evidence') ?? false;
};

/**
 * PostHog multivariate for sessionless Evidence awareness popover (GRO-2896).
 * `control`: no popover / not in experiment. Other keys match {@link PersonalizedEvidenceSuggestionCategory}.
 */
export type EvidenceAwarenessPopupVariant = 'control' | 'floating' | 'sidebar';
const evidenceAwarenessPopupVariantsSchema = z
  .enum(['control', 'floating', 'sidebar'])
  .catch('control');

export const GET_EVIDENCE_AWARENESS_POPUP_VARIANT = (): EvidenceAwarenessPopupVariant => {
  if (getEnvironment() !== ENV_NAME.PROD && typeof window !== 'undefined') {
    const devOverride = getDefaultStore().get(
      sessionlessEvidenceAwarenessDevVariantOverrideAtom
    );
    if (devOverride != null) {
      return evidenceAwarenessPopupVariantsSchema.parse(devOverride);
    }
  }
  return evidenceAwarenessPopupVariantsSchema.parse(
    posthog.getFeatureFlag('exp-evidence-awareness-popup')
  );
};

export const IS_EVIDENCE_ACCESS_OVERRIDE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-access-override') ?? false;
};

export const IS_NHS_EVIDENCE_CONTACT_SALES_ENABLED = () => {
  return posthog.isFeatureEnabled('release-nhs-evidence-contact-sales') ?? false;
};

export const IS_GAMIFY_TRIAL_LENGTH_EVIDENCE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-gamify-trial-length-evidence');
};

export const IS_ADD_MORE_SOURCES_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

export const IS_CHAT_ATTACHMENTS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-chat-attachments');
};

export const IS_CHAT_SUGGESTIONS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-chat-suggestions');
};

export const IS_EVIDENCE_USER_DOCS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-user-docs');
};

export const IS_EVIDENCE_ONLY_ONBOARDING_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

export const IS_EVIDENCE_LIBRARY_ENABLED = () => {
  // Library requires both the library flag AND user docs to be enabled
  return (
    posthog.isFeatureEnabled('release-evidence-library') &&
    IS_EVIDENCE_USER_DOCS_ENABLED()
  );
};

/** Shipped 100% — Flagsmith `release-bulk-upload`. Console flag stays enabled. */
export const IS_BULK_UPLOAD_ENABLED = () => true;

/** PostHog: Evidence activation flow for teams (managed team activation request UI). */
export const IS_EVIDENCE_ACTIVATION_FLOW_FOR_TEAMS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-activation-flow-for-teams') ?? false;
};

/** PostHog: Team settings Access section — per-member / per-role Evidence access for multi-seat teams. */
export const IS_TEAM_EVIDENCE_ACCESS_CONTROLS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-team-evidence-access-controls') ?? false;
};

/**
 * EVI-1957: Chat source control requires ≥1 Evidence source.
 * Sync read for Jotai atoms (includeWebSearchAtom + toggle writers).
 * Prefer `useIsEvidenceChatRequireAtLeastOneSourceEnabled` from
 * `@/app-shell/flags/evidence-chat-require-at-least-one-source` in React components.
 *
 * Shipped 100% — Flagsmith stays enabled. The key stays on
 * `EVIDENCE_CHAT_REQUIRE_AT_LEAST_ONE_SOURCE_FLAG_KEY`.
 */
export const IS_EVIDENCE_CHAT_REQUIRE_AT_LEAST_ONE_SOURCE_ENABLED = () => true;

/**
 * Clinical web drill-in panel inside source control V2 (guidelines toggle +
 * inline country / jurisdiction / specialty edits). Independent rollout gate so
 * the panel can stay OFF in markets where it must not ship (e.g. EU/UK cohorts in PostHog).
 * Default OFF.
 */
export const IS_SOURCE_CONTROL_CLINICAL_WEB_PANEL_ENABLED = () => {
  return posthog.isFeatureEnabled('release-source-control-clinical-web-panel') ?? false;
};

/**
 * GRO-2897: Personalised source logos inside the Sources button.
 * When enabled, the inline mode icons (Stethoscope / BookOpen / LibraryBig / Link2)
 * are replaced with a stack of actual personalised source logos (integration
 * partners + active web-collection favicons).
 */
export const IS_SOURCE_CONTROL_PERSONALISED_LOGOS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-source-control-personalised-logos') ?? false;
};

/**
 * Unified Collections: merges document collections and source collections
 * into a single "collection" concept across the evidence library.
 *
 * Hard-gated OFF for EU/UK (DEV-12160): the unified-collections UI is going
 * through medical-device certification and must not ship to those regions yet.
 * EU/UK users stay on the legacy Library UI; document uploads are unaffected
 * because they are not gated by this flag.
 *
 * Region resolution here is deliberately compliance-grade: we read the
 * authoritative Kinde region straight from the React Query cache (the same
 * source `useIsUKOrEU` uses) and block the feature for regions on an explicit
 * DENY-LIST (`UNIFIED_COLLECTIONS_BLOCKED_REGIONS`). It still FAILS CLOSED on an
 * unresolved/unknown region (stays on the legacy UI). We intentionally do NOT
 * use `getRegion()` — its own docs warn it fails open to AU and honours the
 * spoofable `?region=` query param, so it must never gate a medical-device
 * decision.
 *
 * `reactQueryClient` is resolved lazily (not a top-level import): `flags.ts` is
 * imported almost everywhere, so adding it to the module-load graph reorders
 * initialisation and trips `reactQueryClient` mocks in unrelated suites (and
 * risks a `@/store` import cycle via the query layer). Requiring at call time
 * keeps this near-leaf module dependency-free at load and is safe because every
 * module is initialised by the time the gate is evaluated.
 */
/**
 * Regions where the unified-collections UI is blocked for medical-device
 * certification (DEV-12160). Every other region receives the feature (still
 * subject to the PostHog flag).
 *
 * `SE_AZ1` (Sweden) is included because Sweden is an EU member state, even
 * though its enum value is neither `UK` nor `EU` — a deny-list keyed on only
 * those two values would wrongly let it through. Any future EU/UK region added
 * to the `Region` enum must be added here too.
 */
const UNIFIED_COLLECTIONS_BLOCKED_REGIONS: ReadonlySet<Region> = new Set([
  Region.UK,
  Region.EU,
  Region.SE_AZ1,
]);

const isUnifiedCollectionsRegionAllowed = (): boolean => {
  /* eslint-disable @typescript-eslint/no-require-imports */
  const { reactQueryClient } =
    require('@/utils/reactQueryClient') as typeof import('@/utils/reactQueryClient');
  /* eslint-enable @typescript-eslint/no-require-imports */

  // Guard against unit-test stubs that partially mock the query client.
  if (typeof reactQueryClient.getQueryData !== 'function') return false;

  // Key mirrors GET_KINDE_PROPERTIES in useQueryKindeProperties.ts; inlined as
  // a literal to avoid pulling that module's `@/store` graph into flags.ts.
  const kindeProperties = reactQueryClient.getQueryData<{ region?: Region }>([
    'getKindeProperties',
  ]);
  const region = kindeProperties?.region;

  // Fail closed on an unknown region (pending/unauthenticated); otherwise allow
  // any region that is not explicitly blocked.
  if (!region) return false;
  return !UNIFIED_COLLECTIONS_BLOCKED_REGIONS.has(region);
};

/**
 * Flagsmith key, deliberately renamed from the PostHog `release-unified-collections`.
 * That key has no PostHog flag row and was hardcoded false in #19285; shipped
 * mobile builds still read it, so creating it would switch the feature on
 * fleet-wide (APP-9911). A distinct key keeps the web rollout independent.
 */
export const UNIFIED_COLLECTIONS_FLAG_KEY = 'release-evidence-unified-collections';

export const IS_UNIFIED_COLLECTIONS_ENABLED = () => {
  if (!isUnifiedCollectionsRegionAllowed()) return false;
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    UNIFIED_COLLECTIONS_FLAG_KEY,
    false
  );
};

/** Show Evidence debug panel when enabled via PostHog */
export const IS_EVIDENCE_DEBUG_PANEL_ENABLED = () => {
  return posthog.isFeatureEnabled('evidence-debug-panel');
};

/**
 * Ask Heidi Shortcuts / Protocols: `/` menu and protocols button in Evidence chat.
 *
 * Independent of `release-transcript-chips` (EVI-2114). Always on: the dedicated
 * PostHog flag was never the production gate (chips-on short-circuited this to
 * true), so killing chips must not suddenly depend on a separate 0% flag.
 */
export const IS_ASK_HEIDI_SHORTCUTS_ENABLED = () => {
  return true;
};

/**
 * Floating Ask Heidi Input
 *
 * Enables the new floating Ask Heidi input that appears at the bottom of session pages.
 * Features a pill-shaped input with thread selector, voice input, and choreographed
 * animations when opening the chat sidebar.
 *
 * Independent of `release-transcript-chips` (EVI-2114). Always on for the same
 * reason as shortcuts — chips-on previously short-circuited this gate, and killing
 * chips must not fall back to AskAIV2 and drop the shortcuts surface.
 */
export const IS_FLOATING_ASK_HEIDI_ENABLED = () => {
  return true;
};

export const SHOULD_SHOW_AUTO_ANSWER_TOGGLE = () => {
  return false;
};

/**
 * Ask Heidi History: Shows chat history dropdown in session header.
 * Allows users to view and switch between past Ask Heidi chats for the current session.
 * Independent of `release-transcript-chips` (EVI-2114).
 */
export const IS_ASK_HEIDI_HISTORY_ENABLED = () => {
  if (!IS_ASK_HEIDI_V2_CHAT_COMPLETIONS_ENABLED()) {
    return false;
  }
  return posthog.isFeatureEnabled('release-ask-heidi-history') ?? true;
};

/**
 * Template Library Tabs: Redesigned template library with integrated Favourites tab.
 * When enabled, shows a unified tab system with Favourites as a tab (card layout)
 * instead of a separate section. Also includes full-width search and Uses column.
 */
export const IS_TEMPLATE_LIBRARY_TABS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-template-library-tabs');
};

export const IS_HIDE_SESSION_UPGRADE_BUTTON_EVIDENCE_FAKE_TRIALS_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

export const IS_EVIDENCE_NAVIGATION_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-navigation');
};

/**
 * When enabled, users will enter Evidence trial flow immediately upon signup through the public chat page.
 * Also enables new trial and upgrade flows for new clinician, practice plans.
 * designs: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=7112-8816&m=dev
 */
export const IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-launch-trial-and-upgrade-flows');
};

/**
 * When enabled, shows the redesigned billing page in Evidence settings.
 */
export const IS_EVIDENCE_LAUNCH_BILLING_PAGE_REDESIGN_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-launch-billing-page-redesign');
};

/**
 * When enabled, shows a contextual upgrade modal to power users of Evidence during their
 * Clinician trial, personalised with their Evidence usage count.
 * designs: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=9562-47495&m=dev
 */
export const IS_EVIDENCE_POWER_USER_UPGRADE_MODAL_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

/**
 * Feature flag for the Evidence experience feedback nudge.
 * Shows a feedback prompt after 3-10 queries asking "How useful is Evidence for you?"
 */
export const IS_EVIDENCE_EXPERIENCE_FEEDBACK_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-experience-feedback');
};

/**
 * Evidence Leaderboard: Shows the Evidence leaderboard and referral features.
 * When enabled, users can see their ranking, view leaderboards, and share referral links.
 * Appears in: Settings > Account, Sidebar "Refer & Earn" CTA, /leaderboard page.
 */
export const IS_EVIDENCE_LEADERBOARD_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-leaderboard');
};

/**
 * Evidence-only Referral: When enabled, Evidence-only subscribers (users with active
 * Evidence Pro but no Scribe subscription) see alternate referral copy ($25 instead of $50)
 * and are routed to a separate Rewardful campaign.
 */
export const IS_EVIDENCE_ONLY_REFERRAL_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-only-referral');
};

/**
 * Whether the current user signed up via dictate. Reads the cached user from
 * the query client lazily (mirrors `getCurrentUser` in useQueryUser.ts); a
 * top-level import of that module pulls `@/features/flags` back into `flags.ts`
 * and forms an init cycle that breaks unrelated suites.
 */
const isDictateSignup = (): boolean => {
  /* eslint-disable @typescript-eslint/no-require-imports */
  const { reactQueryClient } =
    require('@/utils/reactQueryClient') as typeof import('@/utils/reactQueryClient');
  /* eslint-enable @typescript-eslint/no-require-imports */

  if (typeof reactQueryClient.getQueryData !== 'function') return false;

  // Key mirrors GET_USER in useQueryUser.ts; inlined as a literal to keep that
  // module's import graph out of flags.ts.
  const user = reactQueryClient.getQueryData<{ signup_source?: SignupSource }>([
    'getUser',
  ]);
  return user?.signup_source === SignupSource.dictate;
};

/**
 * Demo session flow shown after onboarding (5 coach marks).
 * Guides new users through a demo transcription with coach marks.
 * Requires Evidence launch flows and transcript chips to be enabled.
 * English-only for now.
 */
export const IS_DEMO_SESSION_ENABLED = () => {
  if (!isEnglishMarket()) return false;
  if (!IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() && !isDictateSignup())
    return false;
  if (!IS_TRANSCRIPT_CHIPS_ENABLED()) return false;
  return posthog.isFeatureEnabled('release-demo-session');
};

/**
 * Lite demo session flow with 3 coach marks (refine note, create docs, new session).
 * Fallback when transcript chips are off: requires Evidence launch flows but not
 * transcript chips. Only activates when the full demo session flag is OFF.
 * English-only for now.
 */
export const IS_DEMO_SESSION_LITE_ENABLED = () => {
  if (!isEnglishMarket()) return false;
  if (!IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() && !isDictateSignup())
    return false;
  if (IS_DEMO_SESSION_ENABLED()) return false;
  return posthog.isFeatureEnabled('release-demo-session-lite');
};

/**
 * Controls when the evidence sidebar auto-opens relative to transcription:
 * - 'control': do nothing
 * - 'after-transcription': open as soon as transcription is completed
 * - 'during-transcription': open during transcription (when transcription starts)
 */
export const GET_EVIDENCE_SIDEBAR_AUTO_OPEN_VARIANT = ():
  | 'control'
  | 'after-transcription'
  | 'during-transcription' => {
  const schema = z
    .enum(['control', 'after-transcription', 'during-transcription'])
    .catch('control');
  return schema.parse(posthog.getFeatureFlag('experiment-evidence-sidebar-auto-open'));
};

export const IS_EVIDENCE_SBS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-sbs') ?? false;
};

/** Gates chat capacity guard UI: warning/critical/exceeded banners, input blocking, and continue-in-new-chat. */
export const IS_CHAT_CAPACITY_GUARD_ENABLED = () =>
  OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-evidence-chat-capacity-guard',
    false
  );

/** EVI-1493 / EVI-2477: Progressive streaming timeout (15s warning, 30s abort) instead of single 5-min hard timeout. */
export const IS_PROGRESSIVE_STREAM_TIMEOUT_ENABLED = () =>
  OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-evidence-progressive-stream-timeout',
    false
  );

/**
 * DEV-12893 / SWR-171 / EVI-2557: when the browser fires `offline` mid-stream,
 * abort and skip recovery instead of waiting out the timeout. Does not block
 * the send on `navigator.onLine` — Chrome can report offline while the tab is
 * still reaching the API. A real transport failure still surfaces the
 * connectivity notice after the request. Default ON — set
 * `release-evidence-offline-fail-fast` to false to leave the stream running
 * through an `offline` event.
 */
export const IS_EVIDENCE_OFFLINE_FAIL_FAST_ENABLED = () =>
  OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-evidence-offline-fail-fast',
    true
  );

/**
 * Clinical web drill-in panel inside source control V2 (guidelines toggle +
 * inline country / jurisdiction / specialty edits). Rollout/kill gate layered
 * Independent rollout gate — source control V2 stays ON while this panel stays
 * OFF (e.g. EU/UK PostHog cohort exclusion, gradual ramp). Default OFF.
 */
export const useIsSourceControlClinicalWebPanelEnabled = () => {
  return useFeatureFlagEnabled('release-source-control-clinical-web-panel') ?? false;
};

/**
 * Kill switch for the Evidence linked-context provenance gate
 * (`resolveAttachableLinkedContext`): sends refuse staged session/patient ids
 * whose `linkedContextOriginAtom` belongs to a DIFFERENT chat — the fix for
 * new chats born linked to the previously viewed session's patient. The gate
 * relies on every staging writer stamping the origin, so if a missed writer
 * ever leaves legitimate staging refused (context silently not attaching),
 * flip this ON to bypass the origin check and restore the pre-gate raw-atom
 * reads without a deploy. Default OFF (gate active). Kill gate, not an
 * experiment — do not add useManualExposure.
 */
export const useIsLinkedContextProvenanceGateKilled = () => {
  return useBooleanFlagValue('op-evidence-disable-linked-context-provenance-gate', false);
};

/**
 * Feature flag for sharing Evidence chat links.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, classified rather than skipped, on TWO
 * counts. `useFeatureFlagEnabled` resolves through PostHog, which
 * `@heidi/runtime/feature-flags` carries no surface for, so moving it would
 * change WHICH PROVIDER answers (`monorepo-wave-1-p3-feature-flags` Z4). And the
 * two `useManualExposure` pairings make it an EXPERIMENT-backed read: the
 * exposure invariant requires a move to keep the pairing through an injected
 * port on ruling 24's shape, because the emit is an app reaction. Both would have
 * to be answered together, which is a bigger job than a declaration relocation.
 */
export const useIsShareEvidenceChatEnabled = () => {
  const isShareEnabled =
    useFeatureFlagEnabled('release-evidence-enable-share-chat') ?? false;
  const isIntroEnabled =
    useFeatureFlagEnabled('release-evidence-enable-share-chat-intro') ?? false;

  useManualExposure({
    flagKey: 'release-evidence-enable-share-chat',
    isEnabled: isShareEnabled,
    isVisible: true,
  });

  useManualExposure({
    flagKey: 'release-evidence-enable-share-chat-intro',
    isEnabled: isIntroEnabled,
    isVisible: true,
  });

  return isShareEnabled;
};

/**
 * Feature flag for the one-time introduction spotlight that points to the
 * Share chat button in the Evidence chat header. Gated separately from the
 * share feature itself so the intro can be rolled out / rolled back
 * independently (e.g. trial cohorts, gradual release).
 *
 * STAYS APP-SIDE — ruling 36 phase 2. PostHog-resolved, so not door-readable;
 * see `useIsShareEvidenceChatEnabled` above, which reads the same key.
 */
export const useIsShareEvidenceChatIntroEnabled = () => {
  return useFeatureFlagEnabled('release-evidence-enable-share-chat-intro') ?? false;
};

export const DISABLE_SESSION_ASK_HEIDI_BAR_FLAG_KEY =
  'op-scribe-disable-session-ask-heidi-bar';

/**
 * Kill switch for the session-page Ask Heidi bar (desktop floating bar on V1
 * and V2, plus the mobile Ask Heidi button and modal). TRUE hides it. PostHog,
 * default OFF; country/plan targeting lives in PostHog alongside
 * `release-clinical-coding` (EU-27 geoip × free/pro → 100%) for medical-device
 * compliance.
 *
 * Unlike the other `op-*-disable-*` gates this one treats an unresolved flag
 * (`undefined`: flags not yet loaded, or PostHog blocked) as DISABLED, so the
 * bar can never flash for an excluded cohort before targeting resolves. The
 * bar shows only on an explicit `false`. On-prem is unaffected: the compat
 * layer resolves the bundle to a real boolean, and an absent key is `false`.
 */
export const useIsSessionAskHeidiBarDisabled = () => {
  return useFeatureFlagEnabled(DISABLE_SESSION_ASK_HEIDI_BAR_FLAG_KEY) !== false;
};

/**
 * Floating Ask Heidi bar: recentre the bar into the content area during
 * transcription (note/document-like tabs), keeping it idle until the user
 * clicks into it. Bottom-docked everywhere else. PostHog, default OFF.
 */
export const useIsAskHeidiCentreTranscriptionEnabled = () => {
  return useFeatureFlagEnabled('release-ask-heidi-centre-transcription') ?? false;
};

/**
 * Gates the "new chat" CTA dispatching to the v2 (harness-backed)
 * agent-chat creation path instead of the legacy v1 Evidence chat
 * creation path.
 *
 * Existing chats are unaffected by this flag — their version is
 * baked in at creation time and the chat list dispatch reads it from
 * each row, not from a runtime flag. See
 * ``src/features/projects/chatRouting.ts``.
 *
 * Not Tauri-gated like ``useIsProjectsEnabled``: the v2 path itself
 * works in the browser bundle when ``tool_profile === 'evidence_only'``
 * (no desktop client required). Surfaces that mount the
 * ``clinician_desktop`` profile still need to check Tauri separately.
 *
 * Gated on ``useIsRpaAgentEnabled`` so the v2 create path only ramps
 * where the harness-backed agent is live.
 */
export const useIsAgentChatV2Enabled = () => {
  useBooleanFlagValue('release-scribe-agent-chat-v2', false);
  return useIsRpaAgentEnabled();
};

/**
 * When enabled, shows a personalised source recommendation banner above the chat input
 * suggesting evidence sources based on the user's country and specialty.
 *
 * Using PostHog instead of Flagsmith because Flagsmith targeting currently lacks
 * the necessary cohorts for these experiments.
 */
/**
 * Kill switch for the source recommendations endpoint.
 * When enabled, all source recommendation queries are disabled.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, classified rather than skipped, along with
 * the two banner getters below. All three resolve through PostHog, which
 * `@heidi/runtime/feature-flags` carries no surface for, so moving one would
 * change WHICH PROVIDER answers (`monorepo-wave-1-p3-feature-flags` Z4). The
 * banner getter's own body says why the flag is there rather than in Flagsmith,
 * in a commented-out `useBooleanFlagValue` call kept beside the live read. They
 * move when the flags are steered to Flagsmith, not before.
 */
export const useIsSourceRecommendationsKillSwitchEnabled = () => {
  return useFeatureFlagEnabled('kill-evidence-source-recommendations') ?? false;
};

export const useIsSourceRecommendationBannerEnabled = () => {
  // Flagsmith targeting currently lacks the necessary cohorts for this experiment.
  // return useBooleanFlagValue(
  //   'release-evidence-enable-source-recommendation-banner',
  //   false
  // );
  return (
    useFeatureFlagEnabled('release-evidence-enable-source-recommendation-banner') ?? false
  );
};

/**
 * When enabled, shows the source recommendation banner inside the sources panel popover.
 */
export const useIsSourceRecommendationPopoverBannerEnabled = () => {
  return (
    useFeatureFlagEnabled(
      'release-evidence-enable-source-recommendation-popover-banner'
    ) ?? false
  );
};

/**
 * GRO-2767: When enabled, shows a banner after note generation suggesting
 * an Evidence summary based on clinical signal scoring of the note content.
 *
 * Using PostHog instead of Flagsmith because Flagsmith targeting currently lacks
 * the necessary cohorts for these experiments.
 *
 * STAYS APP-SIDE — ruling 36 phase 2. PostHog-resolved, so not door-readable:
 * `@heidi/runtime/feature-flags` carries no PostHog surface and moving it would
 * change WHICH PROVIDER answers (`monorepo-wave-1-p3-feature-flags` Z4). The
 * commented-out `useBooleanFlagValue` call below is the Flagsmith read this
 * would become once the cohorts exist.
 */
export const useIsEvidenceSummarySuggestionEnabled = () => {
  // Flagsmith targeting currently lacks the necessary cohorts for this experiment.
  // return useBooleanFlagValue(
  //   'release-evidence-enable-singular-summary-suggestion',
  //   false
  // );
  return (
    useFeatureFlagEnabled('release-evidence-enable-singular-summary-suggestion') ?? false
  );
};

/**
 * When enabled, shows an evidence prompts bar in the Scribe session surface
 * with personalised clinical suggestions and source avatars.
 *
 * Using PostHog instead of Flagsmith because Flagsmith targeting currently lacks
 * the necessary cohorts for these experiments.
 *
 * STAYS APP-SIDE — ruling 36 phase 2. PostHog-resolved, so not door-readable;
 * same classification as `useIsEvidenceSummarySuggestionEnabled` above, with the
 * same commented-out Flagsmith read below it.
 */
export const useIsEvidencePromptsBarEnabled = () => {
  // Flagsmith targeting currently lacks the necessary cohorts for this experiment.
  // return useBooleanFlagValue('release-evidence-enable-clinical-suggestions-bar', false);
  return (
    useFeatureFlagEnabled('release-evidence-enable-clinical-suggestions-bar') ?? false
  );
};

/**
 * Floating / side-panel Ask Heidi: personalised Sources button (avatar stack,
 * sand/selected fill, tooltip). Same PostHog key as the guided "/" menu
 * follow-up (#17099) — this PR only ships the Sources face.
 *
 * Split from `release-sources-button-states` so this cluster ships independently
 * of centred-bar placement. Rollout/kill gate — useManualExposure is attached
 * at the floating-input call site where the variant is visible.
 */
export const useIsAskHeidiSourcesShortcutsEnabled = () => {
  return useFeatureFlagEnabled('release-ask-heidi-sources-shortcuts') ?? false;
};

/**
 * GRO-2846: When enabled, displays the Evidence search bar in a central location
 * within the empty note area during transcription, with session-stage-based
 * use case chips (before/during/after).
 *
 * Off whenever Work in session is on: the bar hands its prompt to the Evidence
 * chat through `pendingChatMessageAtom`, which a Work side panel never reads.
 * Both reads run unconditionally to keep hook order stable.
 *
 * Using PostHog instead of Flagsmith because Flagsmith targeting currently lacks
 * the necessary cohorts for these experiments.
 *
 * STAYS APP-SIDE — ruling 36 phase 2. PostHog-resolved, so not door-readable;
 * same classification as the two getters above.
 */
export const useIsEvidenceCentralSearchBarEnabled = () => {
  const isWorkInSessionEnabled = useIsWorkInSessionEnabled();
  const isExperimentEnabled =
    useFeatureFlagEnabled('experiment-evidence-central-search-bar') ?? false;
  return isExperimentEnabled && !isWorkInSessionEnabled;
};

/**
 * INC-349 / DEV-12867: remote override for the Evidence readiness fail-open
 * deadline (watchdog + send path). A tuning VALUE, not a kill switch — the
 * fail-open net always fires; this only adjusts WHEN, and the caller clamps
 * the value (`getSourceControlFailOpenTimeoutMs` in
 * `useSourceControlReadinessFailOpen`) so no remote setting can neuter the
 * net or hair-trigger it. Read imperatively, not as a hook: the watchdog
 * arms pre-auth, before the OpenFeature provider may have reconciled, and
 * the baked default must win in that window. Exists because the desktop app
 * bakes the frontend into its bundle — retuning a hard-coded constant there
 * would cost a full desktop release cycle. Flag absent in Flagsmith ⇒
 * `defaultMs` ⇒ behavior identical to the baked constant.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, and the classification is SHAPE rather
 * than provider, which makes it the more interesting of this domain's nine
 * stay-behinds. `@heidi/runtime/feature-flags` publishes `readFlag` (boolean) and
 * `readStringFlag` and NO number reader at all, so there is nothing on the door
 * to move this onto. Reaching for `readFlag` and coercing is not the workaround
 * it looks like: it would change the value's type at the one call site that
 * clamps it. It moves when the door publishes a number read.
 */
export const getEvidenceFailOpenTimeoutOverrideMs = (defaultMs: number) => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getNumberValue(
    'release-evidence-fail-open-timeout-ms',
    defaultMs
  );
};
